import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Map as OlMap, View } from 'ol';
import { Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';
import { DividerModule } from 'primeng/divider';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { SplitterModule } from 'primeng/splitter';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { delay, forkJoin, Observable, retryWhen, scan, Subject } from 'rxjs';
import { PageBaseComponent } from '../../abstracts/page-base/page-base.component';
import { GroupedgraphComponent } from '../../components/groupedgraph/groupedgraph.component';
import { VideoPlayerComponent } from '../../components/video-player/video-player.component';
import { GlobalItem, Graph, GraphGroup, GraphItem } from '../../models/graphItem';
import { Trip } from '../../models/trip';
import { VideoState } from '../../models/video-state';
import { ApiService } from '../../services/api.service';
import { WinterMapService } from '../../services/winter-map-service';

@Component({
  selector: 'app-trip',
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ProgressSpinnerModule,
    SplitterModule,
    DividerModule,
    SliderModule,
    ButtonModule,
    IftaLabelModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    CarouselModule,
    GroupedgraphComponent,
    TabsModule,
    VideoPlayerComponent,
    TranslatePipe
  ],
  providers: [MessageService],
  templateUrl: './trip.component.html',
  styleUrl: './trip.component.css'
})
export class TripComponent extends PageBaseComponent<Trip> implements AfterViewInit {
  private map?: OlMap;
  private location = { lat: 46.71849407521668, lon: 12.223145011825158 };  // Change this as per the location

  isOnPlay: boolean = false;
  playProgress: number = 0;

  private totalMillis: number = 0.0;
  private startMillis: number = 0.0;

  timerValue: string | null = '';
  timestamp: number = 0;

  private readonly delay: number = 500;

  timerTick = signal<VideoState>({
    timeMs: 0,
    playing: false,
    speed: 1
  });

  private intervalId: number | null = null;

  private athleteLayer: VectorLayer<VectorSource> = new VectorLayer();

  speeds = [
    { code: 1,  name: ' 1x' },
    { code: 2,  name: ' 2x' },
    { code: 4,  name: ' 4x' },
    { code: 16, name: '16x' }
  ];

  selectedSpeed = this.speeds[0];

  centerOptions: any[] = [
    { icon: 'fas fa-expand', label: 'On Box', mode: 1 },
    { icon: 'fas fa-compress', label: 'On User', mode: 2 }
  ];

  selectedCenter = this.centerOptions[1];

  get formattedDate() {
    return this.datePipe.transform(this.item.date, "fullDate");
  }

  item: Trip = {
    organizationId: 0,
    teamId: 0,
    sessionId: 0,
    athleteId: 0,
    date: new Date(),
    team: '',
    session: '',
    athlete: '',
    mass: 1.0,
    trackData: {
      minLatitude: 0.0,
      minLongitude: 0.0,
      maxLatitude: 0.0,
      maxLongitude: 0.0,
      start: new Date(),
      end: new Date()
    },
    track: []
  };

  graphs: GraphItem[] = [];
  measures: GlobalItem[] = [];
  grouped: GraphGroup[] = [];

  // NUOVI METADATA VIDEO
  videoStartMs: number = 0;
  videoDurationMs: number = 0;

  initialTime: Date = new Date();

  constructor(apiService: ApiService, messageService: MessageService,
    private winterMap: WinterMapService,
    private router: Router, private datePipe: DatePipe) {
    super(apiService, messageService)
  }

  ngAfterViewInit(): void {
    if (!this.map) {
      const winterLayer = this.winterMap.createWinterTileLayer();

      this.map = new OlMap({
        target: 'map',
        layers: [
          winterLayer,
        ],
        view: new View({
          center: fromLonLat([this.location.lon, this.location.lat]),
          zoom: 15,
        })
      });

    } 
  }

  override ngOnDestroy() {
    this.stop();

    this.map?.dispose();

    super.ngOnDestroy();
  }

  // RICEVE LA DURATA DEL VIDEO
  onVideoMetadataLoaded(data: { durationMs: number }) {
    this.videoDurationMs = data.durationMs;
  }

  // RICEVE I MESSAGGI DI ERRORE
  onMessageReceived(message: string) {
    this.showErrorMessage(message);
  }

  // RICEVE L'ORARIO DI INIZIO VIDEO (offset)
  onVideoStartSelected(offsetMs: number) {
    this.videoStartMs = this.item.trackData.start.getTime() + offsetMs;
  }

  onEnd(): void {
    this.stop();

    const start = this.item.trackData.start.getTime();

    this.timerTick.update(v => ({
      ...v,
      timeMs: start,
      playing: false
    }));
  }

  onPlay(): void {
    if (this.isOnPlay) {
      this.isOnPlay = false;

      this.timerTick.update(v => ({
        ...v,
        playing: false
      }));

      return;
    }

    this.isOnPlay = true;
    this.timerTick.update(v => ({
      ...v,
      playing: true
    }));

    this.intervalId = window.setInterval(() => {
      this.timerTick.update((v) => {
        if (!this.isOnPlay) return v;

        let next = v.timeMs + this.delay * v.speed;

        if (next >= this.item.trackData.end.getTime()) {
          this.stop();
          next = this.item.trackData.end.getTime();
        }

        this.moveAthlete(new Date(next));

        return {
          ...v,
          timeMs: next
        };
      });
    }, this.delay);
  }

  override onSave(): void {
    this.showErrorMessage("Metodo onSave non implementato!")
  }

  onSliderChanged(event: any) {
    if (event.value != this.playProgress) {
      const timeMs = this.startMillis + (event.value * this.totalMillis) / 100.0;

      this.timerTick.update(v => ({
        ...v,
        timeMs: timeMs
      }));

      this.moveAthlete(new Date(timeMs));
    }
  }

  onSpeedChanged() {
    this.timerTick.update(v => ({
      ...v,
      speed: this.selectedSpeed.code
    }));
  }

  onStart(): void {
    const start = this.item.trackData.start.getTime();

    this.timerTick.update(v => ({
      ...v,
      timeMs: start,
      speed: this.selectedSpeed.code
    }));

    this.moveAthlete(this.item.trackData.start);
  }

  private findMax(arr: Graph[]): number {
    if (!Array.isArray(arr) || arr.length === 0) {
      return 0;
    }

    return arr.reduce((max, current) => {
      const maxValue = max.y;
      const currentValue = current.y;

      return currentValue > maxValue ? current : max;
    }).y;
  }

  private findMin(arr: Graph[]): number {
    if (!Array.isArray(arr) || arr.length === 0) {
      return 0;
    }

    return arr.reduce((min, current) => {
      const minValue = min.y;
      const currentValue = current.y;

      return currentValue < minValue ? current : min;
    }).y;
  }

  private groupGraphs(): GraphGroup[] {
    const grouped = new Map<string, GraphGroup>();

    for (const item of this.graphs) {
      const key = `${item.title}__${item.uom}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          title: item.title,
          uom: item.uom,
          xMin: item.xMin,
          xMax: item.xMax,
          yMin: item.yMin,
          yMax: item.yMax,
          signals: [item]
        });
      } else {
        const group = grouped.get(key)!;

        grouped.set(key, {
          ...group,
          xMin: Math.min(group.xMin, item.xMin),
          xMax: Math.max(group.xMax, item.xMax),
          yMin: Math.min(group.yMin, item.yMin),
          yMax: Math.max(group.yMax, item.yMax),
          signals: [...group.signals, item]
        });
      }
    }

    return Array.from(grouped.values());
  }

  private higherMultiple(value: number, reference: number): number {
    return Math.floor(value / reference) * reference + reference;
  }

  protected override loadData(): void {
    this.loading = true;

    const nav = history.state;
    if (nav === undefined) {
      this.loading = false;

      this.showErrorMessage("Parametri non validi!")
      this.router.navigate(['/sessions']);

      return;
    }
    const ids: number[] = [nav.orgId, nav.teamId, nav.sesId, nav.userId];

    this.apiService.getTrip(ids).subscribe({
      next: (t) => {
        if (t === undefined) {
          this.loading = false;
          this.showErrorMessage("Errore nel recupero dei dati");
          return;
        }

        this.item = t;

        // Centro della mappa
        this.location.lat = (t.trackData.minLatitude + t.trackData.maxLatitude) / 2.0;
        this.location.lon = (t.trackData.minLongitude + t.trackData.maxLongitude) / 2.0;

        this.startMillis = t.trackData.start.getTime()
        const endMillis = t.trackData.end.getTime()
        this.totalMillis = endMillis - this.startMillis;

        this.initialTime = t.trackData.start

        if (t.track.length > 0) {
          const userLayer = this.winterMap.createUserTrackLayer(t.track);
          this.athleteLayer = this.winterMap.createAthleteLayer(t.track[0]);
          this.map?.addLayer(userLayer);
          this.map?.addLayer(this.athleteLayer);

          this.map?.getView().setCenter(fromLonLat([t.track[0].longitude, t.track[0].latitude]));

          this.onStart();
        } else {
          this.map?.getView().setCenter(fromLonLat([this.location.lon, this.location.lat]));
        }

        const elevations: Graph[] = t.track.map(p => ({ x: p.date.getTime(), y: p.elevation }))

        this.graphs = [];
        this.graphs.push({
          type: 0,
          title: 'Quota',
          uom: 'm',
          deviceType: 'head',
          order: 0,
          xMin: this.startMillis,
          xMax: endMillis,
          yMax: this.higherMultiple(this.findMax(elevations), 10),
          yMin: this.lowerMultiple(this.findMin(elevations), 10),
          signal: elevations
        });

        forkJoin([
          this.apiService.getGraphMeasures(ids),
          this.apiService.getImuMeasures(ids)
        ]).subscribe({
          next: ([m, i]) => {
            this.graphs = [...this.graphs, ...m, ...i];
            this.grouped = this.groupGraphs();
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
          }
        });

        this.apiService.getGlobalMeasures(ids).subscribe({
          next: (t) => {
            this.measures = [...t];
            this.loading = false;
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
            this.loading = false;
          }
        });

        this.loadSkiTracks();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.showError(err);
      }
    });
  }

  protected override loadSelectedInfo(): Observable<boolean> {
    let data: Subject<boolean> = new Subject<boolean>();
    data.next(true);

    return data;
  }

  private loadSkiTracks(): void {
    const bbox: string = this.winterMap.getBBoxFromBoundaries(this.item.trackData);

    this.winterMap.loadSkiTracks(bbox).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, err) => {
            const retriable = err.status === 429 || err.status === 502 || err.status === 503 || err.status === 504; // Errori di OverPass

            if (!retriable || retryCount >= 3) {
              throw err;
            }

            return retryCount + 1;
          }, 0),
          delay(500)
        )
      )
    ).subscribe({
      next: (overpassData) => {
        const skiLayer = this.winterMap.createSkiTracksLayer(overpassData);
        this.map?.addLayer(skiLayer);
        this.map?.render();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.showError(err);
      }
    });
  }

  private lowerMultiple(value: number, reference: number): number {
    let multiple = Math.floor(value / reference) * reference;

    if (multiple === value) {
      multiple -= reference;
    }

    return multiple;
  }

  private moveAthlete(time: Date) {
    this.timestamp = time.getTime();
    this.timerValue = this.datePipe.transform(time, "mediumTime");
    this.playProgress = ((time.getTime() - this.startMillis) / this.totalMillis) * 100;

    const points = this.item.track.filter(f => f.date <= time);
    if (points !== undefined) {
      const point = points[points.length - 1];
      if (point != undefined) {
        let features = this.athleteLayer.getSource()?.getFeatures();
        let geoMarker = features?.at(0);

        var coord = fromLonLat([point.longitude, point.latitude]);
        geoMarker?.setGeometry(new Point(coord));

        if (this.selectedCenter.mode === 2) this.map?.getView().setCenter(coord);

        this.map?.render;
      }
    }
  };

  protected override resetItems(): void {
    this.isOnPlay = false;
    this.playProgress = 0.0;
    this.timerValue = '';

    this.item = {
      organizationId: 0,
      teamId: 0,
      sessionId: 0,
      athleteId: 0,
      date: new Date(),
      team: '',
      session: '',
      athlete: '',
      mass: 1.0,
      trackData: {
        minLatitude: 0.0,
        minLongitude: 0.0,
        maxLatitude: 0.0,
        maxLongitude: 0.0,
        start: new Date(),
        end: new Date()
      },
      track: []
    }

    this.graphs = [];
    this.measures = [];
    this.grouped = [];
    this.timestamp = 0;
  }

  private stop(): void {
    this.isOnPlay = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
